/**
 * Module pour améliorer les boutons de navigation des modales
 * Migration du script public/js/modal-enhancer.js vers TypeScript
 */

interface ModalElements {
  modal: HTMLElement | null;
  prevBtn: HTMLElement | null;
  nextBtn: HTMLElement | null;
  closeBtn: HTMLElement | null;
  positionIndicator: HTMLElement | null;
}

class ModalEnhancer {
  private observer: MutationObserver | null = null;
  private elements: ModalElements = {
    modal: null,
    prevBtn: null,
    nextBtn: null,
    closeBtn: null,
    positionIndicator: null
  };

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    document.addEventListener('DOMContentLoaded', () => {
      this.enhanceModal();
    });
  }

  private enhanceModal(): void {
    // Attendre que la modale soit présente dans le DOM
    const checkExist = setInterval(() => {
      this.elements.modal = document.getElementById('service-detail-modal');
      if (this.elements.modal) {
        clearInterval(checkExist);
        this.setupObserver();
      }
    }, 100);
  }

  private setupObserver(): void {
    if (!this.elements.modal) return;

    // Observer les changements dans le modal pour détecter quand il devient visible
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' && 
            this.elements.modal?.classList.contains('active')) {
          this.updateButtons();
        }
      });
    });

    this.observer.observe(this.elements.modal, { attributes: true });
  }

  private updateButtons(): void {
    this.enhancePrevButton();
    this.enhanceNextButton();
    this.enhanceCloseButton();
    this.enhancePositionIndicator();
  }

  private enhancePrevButton(): void {
    this.elements.prevBtn = document.getElementById('prev-service-btn');
    if (!this.elements.prevBtn) return;

    this.elements.prevBtn.innerHTML = `
      <svg width="24" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 1L1 8L8 15M1 8H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    this.addHoverEffects(this.elements.prevBtn);
  }

  private enhanceNextButton(): void {
    this.elements.nextBtn = document.getElementById('next-service-btn');
    if (!this.elements.nextBtn) return;

    this.elements.nextBtn.innerHTML = `
      <svg width="24" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 1L23 8L16 15M23 8H1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    this.addHoverEffects(this.elements.nextBtn);
  }

  private enhanceCloseButton(): void {
    this.elements.closeBtn = document.getElementById('modal-close-btn');
    if (!this.elements.closeBtn) return;

    this.elements.closeBtn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 1L1 17M1 1L17 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;

    this.addCloseButtonEffects(this.elements.closeBtn);
  }

  private addHoverEffects(element: HTMLElement): void {
    element.style.transition = 'all 0.3s ease';

    element.addEventListener('mouseover', () => {
      element.style.transform = 'scale(1.1)';
      element.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
      element.style.boxShadow = '0 0 10px rgba(255, 255, 255, 0.2)';
    });

    element.addEventListener('mouseout', () => {
      element.style.transform = '';
      element.style.backgroundColor = '';
      element.style.boxShadow = '';
    });
  }

  private addCloseButtonEffects(element: HTMLElement): void {
    element.style.transition = 'all 0.3s ease';

    element.addEventListener('mouseover', () => {
      element.style.transform = 'rotate(90deg)';
      element.style.backgroundColor = 'rgba(237, 76, 103, 0.3)';
    });

    element.addEventListener('mouseout', () => {
      element.style.transform = '';
      element.style.backgroundColor = '';
    });
  }

  private enhancePositionIndicator(): void {
    this.elements.positionIndicator = document.getElementById('service-position');
    if (!this.elements.positionIndicator) return;

    // Ajouter une animation de pulsation
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes pulse {
        0%, 100% { background-color: rgba(255, 255, 255, 0.1); }
        50% { background-color: rgba(255, 255, 255, 0.2); }
      }
      #service-position {
        animation: pulse 2s infinite;
      }
    `;
    
    if (!document.head.querySelector('style[data-modal-enhancer]')) {
      styleElement.setAttribute('data-modal-enhancer', 'true');
      document.head.appendChild(styleElement);
    }
  }

  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Auto-instantiation
export const modalEnhancer = new ModalEnhancer();
export default ModalEnhancer;